import './LeaderboardPlatformRivals.css';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react"
import { useLocation } from 'react-router-dom';
import { enrichWithPreviousLeaderboard, fetchUnifiedLeaderboardData, parseExcludedSessionIds } from '../../shared/leaderboardShared';
import { fetchDemoLeaderboardData, getTeamPlatform } from './platformRivals';
import logo from './platform-rivals-logo.png';

const TEAMS_PER_PAGE = 20;
const TEAMS_PER_COLUMN = 10;
const ROW_STEP = 70;
const BLANK_ICON = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const PLATFORM_LABELS = {
    console: 'Console',
    pc: 'PC',
    mixed: 'Console et PC',
};

function ConsoleIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6.6 7.5h10.8a4 4 0 0 1 3.9 3.2l.9 4.6a2.3 2.3 0 0 1-4 1.9l-1.8-2.2H7.6l-1.8 2.2a2.3 2.3 0 0 1-4-1.9l.9-4.6a4 4 0 0 1 3.9-3.2z" />
            <path d="M7.6 10.4v3.2M6 12h3.2" />
            <circle cx="15.6" cy="10.9" r="0.4" />
            <circle cx="17.6" cy="12.9" r="0.4" />
        </svg>
    );
}

function PcIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="4" width="18" height="12" rx="1.8" />
            <path d="M12 16v4M8 20h8" />
        </svg>
    );
}

function PlatformBadge({ platform }) {
    return (
        <span className={`prc_platform_badge prc_platform_${platform || 'unknown'}`} title={PLATFORM_LABELS[platform] || 'Plateforme inconnue'}>
            {platform === 'console' && <ConsoleIcon />}
            {platform === 'pc' && <PcIcon />}
        </span>
    );
}

const Row = React.memo(function Row({ rank, teamname, points, elims, avg_place, wins, games, showGamesColumn, onClick, positionChange, refreshDelta, showPositionIndicators, animationEnabled, hasPositionChanged, cascadeFadeEnabled, cascadeIndex, alive, dimmed, showFlags, memberData, platform }) {
    const nameRef = useRef(null);
    const [nameScale, setNameScale] = useState(1);
    const hasFlagMembers = showFlags && memberData && memberData.length > 0;

    useLayoutEffect(() => {
        const element = nameRef.current;
        if (!element || !element.parentElement) return;

        const previous = element.style.transform;
        element.style.transform = 'none';
        const style = window.getComputedStyle(element.parentElement);
        const available = element.parentElement.clientWidth
            - parseFloat(style.paddingLeft)
            - parseFloat(style.paddingRight)
            - (alive ? 20 : 0);
        const needed = element.getBoundingClientRect().width * 1.02;
        element.style.transform = previous;

        setNameScale(available > 0 && needed > available ? Math.max(0.5, available / needed) : 1);
    }, [teamname, memberData, showFlags, alive]);

    const renderPositionChange = () => {
        if (!showPositionIndicators || alive || games < 2 || positionChange === null || positionChange === undefined) {
            return null;
        }
        if (positionChange === 0) {
            return <span className="prc_position_change prc_neutral">=</span>;
        }
        return positionChange > 0
            ? <span className="prc_position_change prc_positive">+{positionChange}</span>
            : <span className="prc_position_change prc_negative">{positionChange}</span>;
    };

    const getAnimationStyle = () => {
        if (!animationEnabled || !hasPositionChanged || !refreshDelta) return {};

        const distance = Math.abs(refreshDelta) * ROW_STEP;
        const duration = Math.max(0.6, Math.min(2.0, distance / 200));

        return {
            '--slide-from': `${refreshDelta > 0 ? distance : -distance}px`,
            '--slide-to': '0px',
            animation: `prcSlideFromTo ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10,
        };
    };

    const rankClass = rank <= 3 ? ` prc_rank_${rank}` : '';

    return (
        <div className={`prc_row_container${rankClass}${dimmed ? ' prc_is_dead' : ''}`} style={cascadeFadeEnabled ? {
            opacity: 0,
            animation: `prcFadeIn 0.6s ${cascadeIndex * 0.08}s forwards`,
        } : getAnimationStyle()}>
            <div className='prc_rank_container'>
                {rank}
                {renderPositionChange()}
            </div>
            <div className='prc_platform_container'>
                <PlatformBadge platform={platform} />
            </div>
            <div className='prc_name_container' onClick={onClick}>
                {alive && <span className='prc_alive_dot' />}
                <span
                    ref={nameRef}
                    className='prc_team_name'
                    style={{ transform: nameScale < 1 ? `scaleX(${nameScale})` : undefined }}
                >
                    {hasFlagMembers ? (
                        memberData.map((member, idx) => (
                            <span key={idx} className='prc_member_with_flag'>
                                <img
                                    src={member.flagUrl}
                                    alt=""
                                    className='prc_flag_icon'
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {idx < memberData.length - 1 && <span className='prc_separator'>-</span>}
                            </span>
                        ))
                    ) : (
                        teamname
                    )}
                </span>
            </div>
            <div className='prc_info_box prc_avg_box'>{avg_place.toFixed(2)}</div>
            <div className='prc_info_box'>{elims}</div>
            <div className='prc_info_box'>{wins}</div>
            <div className='prc_info_box prc_points_box'>{points}</div>
            {showGamesColumn && <div className='prc_info_box'>{games}</div>}
        </div>
    );
});

function LeaderboardPlatformRivals() {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const leaderboard_id = urlParams.get('id');
    const demoMode = urlParams.get('demo') === 'true';
    const showFlags = urlParams.get('flags') === 'true';
    const showGamesColumn = urlParams.get('games') === 'true';
    const titleText = urlParams.get('title') || 'Classement';
    const subtitleText = urlParams.get('subtitle') ?? 'Console vs PC · Duo';
    const rotateSeconds = Number.parseInt(urlParams.get('rotate') || '', 10);
    const excludedSessionIds = useMemo(() => parseExcludedSessionIds(new URLSearchParams(location.search)), [location.search]);
    const excludedSessionIdsKey = useMemo(() => Array.from(excludedSessionIds).sort().join(','), [excludedSessionIds]);

    const [leaderboard, setLeaderboard] = useState([]);
    const [teamDetails, setTeamDetails] = useState({});
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(urlParams.get('cascade') === 'true');
    const previousLeaderboardRef = useRef(null);

    const loadLeaderboard = async () => {
        try {
            const data = demoMode
                ? fetchDemoLeaderboardData()
                : await fetchUnifiedLeaderboardData({
                    leaderboardId: leaderboard_id,
                    excludedSessionIds,
                    showFlags,
                    forceRankByPoints: true,
                    includeV7: true,
                    indicatorsOnlyWhenAllDead: true,
                });

            setShowPositionIndicators(data.showPositionIndicators);

            const previousData = previousLeaderboardRef.current;
            const merged = enrichWithPreviousLeaderboard(data.leaderboard, previousData);
            const previousPlaces = new Map((previousData || []).map((team) => [team.teamname, team.place]));
            const rows = merged.leaderboard.map((team) => {
                const previousPlace = previousPlaces.get(team.teamname);
                return {
                    ...team,
                    platform: getTeamPlatform(data.teamDetails[team.teamname]?.members),
                    refreshDelta: typeof previousPlace === 'number' ? previousPlace - team.place : 0,
                };
            });

            setLeaderboard(rows);
            setTeamDetails(data.teamDetails);
            previousLeaderboardRef.current = rows;

            if (previousData && merged.changedCount > 0) {
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }
        } catch (error) {
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const previousTitle = document.title;
        const icons = Array.from(document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']"));
        const previousHrefs = icons.map((icon) => icon.getAttribute('href'));
        document.title = 'Platform Rivals Cup';
        icons.forEach((icon) => icon.setAttribute('href', BLANK_ICON));
        return () => {
            document.title = previousTitle;
            icons.forEach((icon, index) => icon.setAttribute('href', previousHrefs[index]));
        };
    }, []);

    useEffect(() => {
        loadLeaderboard();
        const interval = setInterval(loadLeaderboard, 15000);
        return () => clearInterval(interval);
    }, [leaderboard_id, demoMode, showFlags, excludedSessionIdsKey]);

    const filteredLeaderboard = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return leaderboard;
        return leaderboard.filter((team) => {
            if (team.teamname.toLowerCase().includes(query)) return true;
            if (!Number.isNaN(Number(query)) && team.place === Number(query)) return true;
            return (teamDetails[team.teamname]?.members || []).some((member) =>
                String(member.ingame_name || '').toLowerCase().includes(query)
            );
        });
    }, [leaderboard, teamDetails, searchQuery]);

    const pageCount = Math.max(1, Math.ceil(filteredLeaderboard.length / TEAMS_PER_PAGE));
    const currentPage = Math.min(page, pageCount - 1);

    const nextPage = () => setPage((current) => Math.min(current + 1, pageCount - 1));
    const previousPage = () => setPage((current) => Math.max(current - 1, 0));

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    useEffect(() => {
        if (!Number.isFinite(rotateSeconds) || rotateSeconds <= 0 || pageCount <= 1) return undefined;
        const interval = setInterval(() => {
            setPage((current) => (current + 1) % pageCount);
        }, rotateSeconds * 1000);
        return () => clearInterval(interval);
    }, [rotateSeconds, pageCount]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch((previous) => !previous);
            } else if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled((previous) => !previous);
            } else if (event.key === 'ArrowRight' && event.target.tagName !== 'INPUT') {
                setPage((current) => Math.min(current + 1, pageCount - 1));
            } else if (event.key === 'ArrowLeft' && event.target.tagName !== 'INPUT') {
                setPage((current) => Math.max(current - 1, 0));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pageCount]);

    const pageStart = currentPage * TEAMS_PER_PAGE;
    const leftItems = filteredLeaderboard.slice(pageStart, pageStart + TEAMS_PER_COLUMN);
    const rightItems = filteredLeaderboard.slice(pageStart + TEAMS_PER_COLUMN, pageStart + TEAMS_PER_PAGE);
    const hasAliveTeams = leaderboard.some((team) => team.alive);
    const selectedDetails = selectedTeam ? teamDetails[selectedTeam] : null;
    const selectedSessions = [...(selectedDetails?.sessions || [])]
        .sort((a, b) => (Number(a.start_time) || 0) - (Number(b.start_time) || 0));
    const finishedPlaces = selectedSessions.map((s) => s.place).filter((place) => place > 0);

    const renderHeader = () => (
        <div className='prc_header_container'>
            <div className='prc_rank_header' onClick={previousPage}>#</div>
            <div className='prc_platform_header' />
            <div className='prc_name_header'>Équipe</div>
            <div className='prc_info_header prc_avg_header'>Moy.</div>
            <div className='prc_info_header'>Élims</div>
            <div className='prc_info_header'>Top 1</div>
            <div className='prc_info_header prc_points_header' onClick={nextPage}>Pts</div>
            {showGamesColumn && <div className='prc_info_header' onClick={nextPage}>Games</div>}
        </div>
    );

    const renderColumn = (items, offset) => (
        <div className='prc_leaderboard_column'>
            {renderHeader()}
            {items.map((data, index) => (
                <Row
                    key={`${data.teamId || data.teamname}-${data.place}`}
                    rank={data.place}
                    teamname={data.teamname}
                    points={data.points}
                    elims={data.elims}
                    wins={data.wins}
                    games={data.games}
                    avg_place={data.avg_place}
                    showGamesColumn={showGamesColumn}
                    onClick={() => setSelectedTeam(data.teamname)}
                    positionChange={data.positionChange}
                    refreshDelta={data.refreshDelta || 0}
                    showPositionIndicators={showPositionIndicators}
                    animationEnabled={animationEnabled && data.hasPositionChanged}
                    hasPositionChanged={data.hasPositionChanged || false}
                    cascadeFadeEnabled={cascadeFadeEnabled}
                    cascadeIndex={offset + index}
                    alive={data.alive}
                    dimmed={hasAliveTeams && !data.alive}
                    showFlags={showFlags}
                    memberData={data.memberData}
                    platform={data.platform}
                />
            ))}
        </div>
    );

    return (
        <div className='platform_rivals'>
            <div className='prc_leaderboard_container'>
                {showSearch && (
                    <div className='prc_search_container'>
                        <input
                            type='text'
                            className='prc_search_input'
                            value={searchQuery}
                            placeholder='Rechercher un joueur'
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>
                )}

                <header className='prc_top_bar'>
                    <div className='prc_title_block'>
                        <span className='prc_title_main'>{titleText}</span>
                        {subtitleText && <span className='prc_title_sub'>{subtitleText}</span>}
                    </div>
                    <img className='prc_event_logo' src={logo} alt='Platform Rivals Cup' />
                    <div className='prc_legend'>
                        <span className='prc_legend_item'><PlatformBadge platform='console' />Console</span>
                        <span className='prc_legend_item'><PlatformBadge platform='pc' />PC</span>
                    </div>
                </header>

                <div className='prc_dual_leaderboard'>
                    {renderColumn(leftItems, 0)}
                    {rightItems.length > 0 && renderColumn(rightItems, TEAMS_PER_COLUMN)}
                </div>

                {pageCount > 1 && (
                    <div className='prc_page_indicator'>
                        {Array.from({ length: pageCount }, (_, index) => (
                            <span
                                key={index}
                                className={`prc_page_dot${index === currentPage ? ' prc_is_active' : ''}`}
                                onClick={() => setPage(index)}
                            />
                        ))}
                    </div>
                )}

                {selectedDetails && (
                    <div className='prc_modal_overlay' onClick={() => setSelectedTeam(null)}>
                        <div className='prc_modal_content' onClick={(e) => e.stopPropagation()}>
                            <button className='prc_close_button' onClick={() => setSelectedTeam(null)}>×</button>
                            <div className='prc_modal_header'>
                                <span className='prc_modal_rank'>#{selectedDetails.teamData.place}</span>
                                <div className='prc_modal_members'>
                                    {selectedDetails.members.map((member, index) => (
                                        <div key={index} className='prc_member_item'>
                                            <PlatformBadge platform={getTeamPlatform([member])} />
                                            <span className='prc_member_name'>{member.ingame_name || member.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='prc_modal_stats'>
                                <div className='prc_stat_item'><span className='prc_stat_value'>{selectedDetails.teamData.points}</span><span className='prc_stat_label'>Points</span></div>
                                <div className='prc_stat_item'><span className='prc_stat_value'>{selectedSessions.length}</span><span className='prc_stat_label'>Games</span></div>
                                <div className='prc_stat_item'><span className='prc_stat_value'>{selectedSessions.filter((s) => s.place === 1).length}</span><span className='prc_stat_label'>Top 1</span></div>
                                <div className='prc_stat_item'><span className='prc_stat_value'>{selectedSessions.reduce((acc, s) => acc + (Number(s.kills) || 0), 0)}</span><span className='prc_stat_label'>Élims</span></div>
                                <div className='prc_stat_item'><span className='prc_stat_value'>{finishedPlaces.length ? Math.min(...finishedPlaces) : '-'}</span><span className='prc_stat_label'>Meilleure place</span></div>
                            </div>
                            <div className='prc_sessions_table'>
                                <div className='prc_session_row prc_session_header'>
                                    <div>Game</div><div>Place</div><div>Élims</div>
                                </div>
                                {selectedSessions.map((session, index) => (
                                    <div key={index} className='prc_session_row'>
                                        <div>{index + 1}</div>
                                        <div className={session.place > 0 ? (session.place === 1 ? 'prc_place_win' : '') : 'prc_place_live'}>
                                            {session.place > 0 ? session.place : 'En jeu'}
                                        </div>
                                        <div>{session.kills}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LeaderboardPlatformRivals
